import { cn } from "@u/cn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface SortCycleOption<T extends string> {
  value: T;
  label: string;
  icon: LucideIcon;
}

/**
 * Single icon button that cycles through sort options (newest → oldest → …),
 * mirroring the leads toolbar. Icon-only on mobile; shows the "sort" label on
 * desktop. Tooltip/aria announce the active option. Caller owns the option set
 * (per-surface fields differ) and the value/onChange.
 */
export function SortCycleButton<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: SortCycleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const idx = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const cur = options[idx];
  const Icon = cur.icon;
  const cycle = () => onChange(options[(idx + 1) % options.length].value);

  const name = `${t("sort_by")}: ${cur.label}`;

  const button = (
    <button
      type="button"
      onClick={cycle}
      aria-label={name}
      className={cn(
        "h-8 shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-border/60 px-2 sm:px-3 text-xs text-muted-foreground hover:text-foreground transition-colors",
        className,
      )}
    >
      <Icon size={15} className="shrink-0" />
      {/* The static "sort by" alone read as a dead label — say what it's sorted
          by. Kept whole (no truncate): a half-word like "Má…" is unreadable, so
          below lg it collapses to icon-only + tooltip; from lg the flexible
          search box yields space instead of garbling this. */}
      <span className="hidden lg:inline whitespace-nowrap">
        <span className="text-muted-foreground/70">{t("sort_by")}:</span>{" "}
        <span className="text-foreground">{cur.label}</span>
      </span>
    </button>
  );

  // Names the button on hover while the label is collapsed (below lg).
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom" className="lg:hidden">
        {name}
      </TooltipContent>
    </Tooltip>
  );
}
